import { getP } from 'redux-polyglot'
import {
  INPUT_BILLING_ADDRESS,
  INPUT_BILLING_CITY,
  INPUT_BILLING_COUNTRY,
  INPUT_BILLING_STATE,
  INPUT_BILLING_ZIP,
  INPUT_CC_EXPIRATION_MONTH,
  INPUT_CC_EXPIRATION_YEAR,
  INPUT_CC_NUMBER,
  INPUT_CC_SECURITY_CODE,
  INPUT_EMAIL_ADDRESS,
  INPUT_FIRST_NAME,
  INPUT_LAST_NAME,
  INPUT_PHONE_NUMBER
} from '../constants/billing'
import api from '../libs/api'
import {
  getBillingAddress,
  getBillingCity,
  getBillingCountryCode,
  getBillingState,
  getBillingZip,
  getCCExpirationMonth,
  getCCExpirationYear,
  getCCNumber,
  getCCSecurityCode,
  getEmailAddress,
  getFirstName,
  getLastName,
  getPhoneNumber
} from '../selectors/billing'
import { showToast } from './app'

/**
 * Actions related to managing input fields
 */
export const FOCUS_INPUT = 'BILLING/FOCUS_INPUT'
export const UPDATE_INPUT = 'BILLING/UPDATE_INPUT'
export const DELETE_INPUT = 'BILLING/DELETE_INPUT'
export const ERROR_INPUT = 'BILLING/ERROR_INPUT'
export const SET_VALUE_INPUT = 'BILLING/SET_VALUE_INPUT'

export const focusInput = (focusedInput, focusedRow, focusedCol) => {

  let newFocusedInput
  switch(focusedInput) {
    case INPUT_CC_NUMBER:
    case INPUT_CC_EXPIRATION_MONTH:
    case INPUT_CC_EXPIRATION_YEAR:
    case INPUT_CC_SECURITY_CODE:
    case INPUT_FIRST_NAME:
    case INPUT_LAST_NAME:
    case INPUT_EMAIL_ADDRESS:
    case INPUT_PHONE_NUMBER:
    case INPUT_BILLING_ADDRESS:
    case INPUT_BILLING_COUNTRY:
    case INPUT_BILLING_ZIP:
    case INPUT_BILLING_CITY:
    case INPUT_BILLING_STATE:
      newFocusedInput = focusedInput
      break;
    default:
      newFocusedInput = INPUT_CC_NUMBER
  }

  return {
    type: FOCUS_INPUT,
    payload: {
      focusedInput: newFocusedInput,
      focusedRow: focusedRow,
      focusedCol: focusedCol
    }
  }
}

export const updateInput = newKey => ({
  type: UPDATE_INPUT,
  payload: {
    newKey
  }
})

export const deleteInput = () => ({
  type: DELETE_INPUT
})

export const errorInput = (input, error) => ({
  type: ERROR_INPUT,
  payload: {
    input,
    error
  }
})

export const setValueInput = (input, value) => ({
  type: SET_VALUE_INPUT,
  payload: {
    input,
    value
  }
})

/**
 * Actions related to setting status
 */
export const SET_STATUS = 'BILLING/SET_STATUS'

export const setStatus = (status) => ({
  type: SET_STATUS,
  payload: {
    status
  }
})

/**
 * Actions related to focusing keyboard
 */
export const FOCUS_KEYBOARD = 'BILLING/FOCUS_KEYBOARD'

export const focusKeyboard = (focus) => ({
  type: FOCUS_KEYBOARD,
  payload: {
    focus
  }
})

/**
 * Actions for retrieving billing details
 */
export const BILLING_DETAILS_REQUEST = 'ACCOUNT/BILLING/REQUEST'
export const BILLING_DETAILS_SUCCESS = 'ACCOUNT/BILLING/SUCCESS'
export const BILLING_DETAILS_FAILURE = 'ACCOUNT/BILLING/FAILURE'

const billingDetailsRequest = () => ({
  type: BILLING_DETAILS_REQUEST
})

const billingDetailsSuccess = (billingDetails) => ({
  type: BILLING_DETAILS_SUCCESS,
  payload: billingDetails
})

const billingDetailsFailure = (error) => ({
  type: BILLING_DETAILS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const loadBillingDetails = () => (dispatch, getState) => {
  dispatch(billingDetailsRequest())
  return api.getBillingDetails()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (response.billing !== null) {
            return dispatch(billingDetailsSuccess(response.billing))
          } else {
            return dispatch(billingDetailsFailure(getP(getState()).t("tv_account.error_no_billing_details", { _: "No billing details have been registered on your account." })))
          }
        } else {
          return dispatch(billingDetailsFailure(response.reason))
        }
      },
      () => dispatch(billingDetailsFailure(getP(getState()).t("tv_account.error_load_billing_details", { _: "An error occurred while loading billing details." })))
    )
}

/**
 * Actions related to updating the billing details
 */
export const UPDATE_BILLING_REQUEST = 'ACCOUNT/BILLING/UPDATE_REQUEST'
export const UPDATE_BILLING_SUCCESS = 'ACCOUNT/BILLING/UPDATE_SUCCESS'
export const UPDATE_BILLING_FAILURE = 'ACCOUNT/BILLING/UPDATE_FAILURE'

const updateBillingDetailsRequest = () => ({
  type: UPDATE_BILLING_REQUEST
})

const updateBillingDetailsFailure = (error) => ({
  type: UPDATE_BILLING_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const updateBillingDetailsSuccess = () => ({
  type: UPDATE_BILLING_SUCCESS
})

export const updateBillingDetails = () => (dispatch, getState) => {
  dispatch(updateBillingDetailsRequest())

  const month = parseInt(getCCExpirationMonth(getState()), 10)
      let year = parseInt(getCCExpirationYear(getState()), 10)
      if (year < 100) {
        year += 2000
      }

  const email = getEmailAddress(getState())
  const billingDetails = {
    number: getCCNumber(getState()),
    month: month,
    year: year,
    code: getCCSecurityCode(getState()),
    first_name: getFirstName(getState()),
    last_name: getLastName(getState()),
    email: email,
    phone: getPhoneNumber(getState()),
    address: getBillingAddress(getState()),
    country: getBillingCountryCode(getState()),
    postcode: getBillingZip(getState()),
    city: getBillingCity(getState()),
    state: getBillingState(getState())
  }

  return api.updateBillingDetails(billingDetails)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return Promise.all([
            dispatch(updateBillingDetailsSuccess()),
            dispatch(showToast(getP(getState()).t("tv_account.success_update_billing_details", { _: "Your billing details have been successfully updated" })))
          ])
        } else {
          return dispatch(updateBillingDetailsFailure(response.reason))
        }
      },
      () => dispatch(updateBillingDetailsFailure(getP(getState()).t("tv_account.error_update_billing_details", { _: "An error has occurred while updating the billing details." })))
    )
}
